import { ChevronDown, ChevronUp, MessagesSquare } from "@tamagui/lucide-icons";
import { useId, useState } from "react";

import { Button, Text, View, YStack, useTheme } from "@/components";
import { brandName } from "@/config/brand";

// Mirrors SMS_HANDOFF_SEED_PREFIX in superclinical-be/app/constants.py.
export const SMS_HANDOFF_SEED_PREFIX = "From our earlier conversation:";

export const isSmsHandoffSeed = (content: string) =>
  content.startsWith(SMS_HANDOFF_SEED_PREFIX);

// The backend labels turns for the model; show them from the patient's point of view, with a
// blank line between turns.
const toDisplayTranscript = (transcript: string) =>
  transcript
    .replace(/\n(?=(Patient|Assistant): )/g, "\n\n")
    .replace(/^Patient: /gm, "You: ")
    .replace(/^Assistant: /gm, `${brandName}: `);

const SmsHandoffSeed = ({ content }: { content: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const theme = useTheme();
  const transcript = toDisplayTranscript(
    content.slice(SMS_HANDOFF_SEED_PREFIX.length).trim(),
  );
  const Chevron = isOpen ? ChevronUp : ChevronDown;

  return (
    <YStack
      borderRadius="$150"
      outlineWidth={2}
      outlineColor="$strokeNeutral"
      outlineStyle="solid"
      marginVertical="$100"
    >
      <Button.Wrapper
        gap="$200"
        padding="$300"
        alignItems="center"
        onPress={() => setIsOpen((open) => !open)}
        dataTrackingId="sms-handoff-seed-toggle"
        aria-label="Toggle your earlier text conversation"
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <View flexShrink={0}>
          <MessagesSquare color={theme.iconBase.get()} />
        </View>
        <Text semibold flex={1}>
          Picked up from your text conversation
        </Text>
        <Chevron flexShrink={0} size="$200" color="$textSecondary" />
      </Button.Wrapper>
      {isOpen && (
        <YStack id={panelId} paddingHorizontal="$300" paddingBottom="$300">
          <Text size="$sm" color="$textSecondary" whiteSpace="pre-line">
            {transcript}
          </Text>
        </YStack>
      )}
    </YStack>
  );
};

export default SmsHandoffSeed;
